import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  FileText,
  Plus,
  Search,
  MoreHorizontal,
  Edit,
  Trash2,
  Copy,
  Eye,
  Star,
  Settings,
  Palette,
} from "lucide-react";
import { InvoiceTemplate } from "@/types/invoice";
import { useToast } from "@/hooks/use-toast";

interface InvoiceTemplateManagerProps {
  templates: InvoiceTemplate[];
  loading?: boolean;
  onCreateTemplate: () => void;
  onEditTemplate: (template: InvoiceTemplate) => void;
  onDeleteTemplate: (template: InvoiceTemplate) => void;
  onDuplicateTemplate: (template: InvoiceTemplate) => void;
  onPreviewTemplate: (template: InvoiceTemplate) => void;
  onSetDefaultTemplate: (template: InvoiceTemplate) => void;
  onRefresh: () => void;
}

const InvoiceTemplateManager: React.FC<InvoiceTemplateManagerProps> = ({
  templates,
  loading = false,
  onCreateTemplate,
  onEditTemplate,
  onDeleteTemplate,
  onDuplicateTemplate,
  onPreviewTemplate,
  onSetDefaultTemplate,
  onRefresh,
}) => {
  const { t } = useTranslation();
  const { toast } = useToast();

  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [templateToDelete, setTemplateToDelete] =
    useState<InvoiceTemplate | null>(null);

  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    }).format(date);
  };

  // Filter templates based on search and status
  const filteredTemplates = templates.filter((template) => {
    const matchesSearch =
      template.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      template.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      template.companyName?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus =
      statusFilter === "all" ||
      (statusFilter === "active" && template.isActive) ||
      (statusFilter === "inactive" && !template.isActive) ||
      (statusFilter === "default" && template.isDefault);

    return matchesSearch && matchesStatus;
  });

  const handleDeleteTemplate = (template: InvoiceTemplate) => {
    if (template.isDefault) {
      toast({
        title: t("templates.cannotDeleteDefault"),
        description: t("templates.cannotDeleteDefaultDescription"),
        variant: "destructive",
      });
      return;
    }

    setTemplateToDelete(template);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = () => {
    if (templateToDelete) {
      onDeleteTemplate(templateToDelete);
      setDeleteDialogOpen(false);
      setTemplateToDelete(null);
    }
  };

  const handleSetDefault = (template: InvoiceTemplate) => {
    if (!template.isActive) {
      toast({
        title: t("templates.cannotSetInactiveDefault"),
        description: t("templates.cannotSetInactiveDefaultDescription"),
        variant: "destructive",
      });
      return;
    }

    onSetDefaultTemplate(template);
  };

  const getStatusBadge = (template: InvoiceTemplate) => {
    if (template.isDefault) {
      return (
        <Badge variant="default" className="bg-blue-500 hover:bg-blue-600">
          <Star className="h-3 w-3 mr-1" />
          {t("templates.default")}
        </Badge>
      );
    }

    if (template.isActive) {
      return (
        <Badge variant="default" className="bg-green-500 hover:bg-green-600">
          {t("common.active")}
        </Badge>
      );
    }

    return <Badge variant="secondary">{t("common.inactive")}</Badge>;
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            {t("templates.title")}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {/* Search and Filter Controls */}
          <div className="flex flex-col sm:flex-row gap-4 mb-6">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <Input
                  placeholder={t("templates.searchPlaceholder")}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>

            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-48">
                <SelectValue placeholder={t("templates.filterByStatus")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("common.all")}</SelectItem>
                <SelectItem value="active">{t("common.active")}</SelectItem>
                <SelectItem value="inactive">{t("common.inactive")}</SelectItem>
                <SelectItem value="default">
                  {t("templates.default")}
                </SelectItem>
              </SelectContent>
            </Select>

            <Button onClick={onCreateTemplate}>
              <Plus className="h-4 w-4 mr-2" />
              {t("templates.create")}
            </Button>

            <Button variant="outline" onClick={onRefresh}>
              <Settings className="h-4 w-4 mr-2" />
              {t("common.refresh")}
            </Button>
          </div>

          {/* Templates Table */}
          <div className="border rounded-lg">
            <ScrollArea className="h-[500px]">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t("templates.name")}</TableHead>
                    <TableHead>{t("templates.description")}</TableHead>
                    <TableHead>{t("templates.company")}</TableHead>
                    <TableHead>{t("templates.colors")}</TableHead>
                    <TableHead>{t("templates.status")}</TableHead>
                    <TableHead>{t("templates.lastModified")}</TableHead>
                    <TableHead className="text-right">
                      {t("common.actions")}
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8">
                        {t("common.loading")}
                      </TableCell>
                    </TableRow>
                  ) : filteredTemplates.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={7}
                        className="text-center py-8 text-gray-500"
                      >
                        {searchTerm || statusFilter !== "all"
                          ? t("templates.noResults")
                          : t("templates.empty")}
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredTemplates.map((template) => (
                      <TableRow key={template.id}>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <div>
                              <div className="font-medium">{template.name}</div>
                              {template.isDefault && (
                                <div className="text-xs text-blue-600">
                                  {t("templates.defaultTemplate")}
                                </div>
                              )}
                            </div>
                          </div>
                        </TableCell>

                        <TableCell>
                          <div className="max-w-xs">
                            {template.description ? (
                              <span className="text-sm text-muted-foreground">
                                {template.description}
                              </span>
                            ) : (
                              <span className="text-sm text-muted-foreground italic">
                                {t("templates.noDescription")}
                              </span>
                            )}
                          </div>
                        </TableCell>

                        <TableCell>
                          {template.companyName ? (
                            <div>
                              <div className="font-medium text-sm">
                                {template.companyName}
                              </div>
                              {template.companyEmail && (
                                <div className="text-xs text-muted-foreground">
                                  {template.companyEmail}
                                </div>
                              )}
                            </div>
                          ) : (
                            <span className="text-sm text-muted-foreground italic">
                              {t("templates.noCompanyInfo")}
                            </span>
                          )}
                        </TableCell>

                        <TableCell>
                          <div className="flex items-center gap-2">
                            <div
                              className="w-4 h-4 rounded border"
                              style={{ backgroundColor: template.primaryColor }}
                              title={`Primary: ${template.primaryColor}`}
                            />
                            <div
                              className="w-4 h-4 rounded border"
                              style={{
                                backgroundColor: template.secondaryColor,
                              }}
                              title={`Secondary: ${template.secondaryColor}`}
                            />
                            <span className="text-xs text-muted-foreground">
                              {template.fontFamily}
                            </span>
                          </div>
                        </TableCell>

                        <TableCell>{getStatusBadge(template)}</TableCell>

                        <TableCell>
                          <div className="text-sm">
                            {formatDate(template.updatedAt)}
                          </div>
                        </TableCell>

                        <TableCell className="text-right">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" className="h-8 w-8 p-0">
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem
                                onClick={() => onPreviewTemplate(template)}
                              >
                                <Eye className="h-4 w-4 mr-2" />
                                {t("templates.preview")}
                              </DropdownMenuItem>

                              <DropdownMenuItem
                                onClick={() => onEditTemplate(template)}
                              >
                                <Edit className="h-4 w-4 mr-2" />
                                {t("common.edit")}
                              </DropdownMenuItem>

                              <DropdownMenuItem
                                onClick={() => onDuplicateTemplate(template)}
                              >
                                <Copy className="h-4 w-4 mr-2" />
                                {t("templates.duplicate")}
                              </DropdownMenuItem>

                              {!template.isDefault && (
                                <DropdownMenuItem
                                  onClick={() => handleSetDefault(template)}
                                >
                                  <Star className="h-4 w-4 mr-2" />
                                  {t("templates.setAsDefault")}
                                </DropdownMenuItem>
                              )}

                              {!template.isDefault && (
                                <DropdownMenuItem
                                  onClick={() => handleDeleteTemplate(template)}
                                  className="text-red-600"
                                >
                                  <Trash2 className="h-4 w-4 mr-2" />
                                  {t("common.delete")}
                                </DropdownMenuItem>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </ScrollArea>
          </div>

          {/* Summary Stats */}
          <div className="flex justify-between items-center mt-4 text-sm text-muted-foreground">
            <div>
              {t("templates.totalTemplates", { count: templates.length })}
            </div>
            <div className="flex gap-4">
              <span>
                {t("templates.activeTemplates", {
                  count: templates.filter((t) => t.isActive).length,
                })}
              </span>
              <span>
                {t("templates.defaultTemplates", {
                  count: templates.filter((t) => t.isDefault).length,
                })}
              </span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("templates.deleteTemplate")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("templates.deleteConfirmation", {
                name: templateToDelete?.name,
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              {t("common.delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default InvoiceTemplateManager;
